import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API_BASE_URL } from '../config/api.config';
import { Alerta, AlertaEstado, AlertaSeveridad, AlertaTipo } from '../models';

/** Fila tal como la devuelve `GET /alerts` (contrato HTTP en inglés). */
interface AlertaApi {
  id: number;
  type: AlertaTipo;
  severity: AlertaSeveridad;
  status: AlertaEstado;
  title: string;
  detail: string | null;
  invoiceId: number | null;
  cessionId: number | null;
  data: Record<string, unknown> | null;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

/**
 * Lectura de alertas de inconsistencias (panel de vigilancia, §7) contra el backend real
 * (`GET /alerts`). El reconocimiento (`PATCH /alerts/:id/acknowledge`) es acción de ops
 * (rol TIC) y queda en auditoría del backend.
 */
@Injectable({ providedIn: 'root' })
export class AlertasService {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_BASE_URL}/alerts`;
  private readonly orden: Record<AlertaSeveridad, number> = { critica: 0, advertencia: 1, info: 2 };

  getTodas(): Observable<Alerta[]> {
    return this.http
      .get<AlertaApi[]>(this.base)
      .pipe(map((filas) => this.ordenadas(filas.map((f) => this.aDominio(f)))));
  }

  getAbiertas(): Observable<Alerta[]> {
    const params = new HttpParams().set('status', 'abierta');
    return this.http
      .get<AlertaApi[]>(this.base, { params })
      .pipe(map((filas) => this.ordenadas(filas.map((f) => this.aDominio(f)))));
  }

  reconocer(id: number): Observable<Alerta> {
    return this.http
      .patch<AlertaApi>(`${this.base}/${id}/acknowledge`, {})
      .pipe(map((f) => this.aDominio(f)));
  }

  private aDominio(f: AlertaApi): Alerta {
    return {
      id: f.id,
      tipo: f.type,
      severidad: f.severity,
      estado: f.status,
      titulo: f.title,
      detalle: f.detail,
      facturaId: f.invoiceId,
      cesionId: f.cessionId,
      datos: f.data,
      creadoEn: f.createdAt,
      reconocidoEn: f.acknowledgedAt,
      reconocidoPor: f.acknowledgedBy,
      resueltoEn: f.resolvedAt,
      resueltoPor: f.resolvedBy,
    };
  }

  private ordenadas(data: Alerta[]): Alerta[] {
    return [...data].sort(
      (a, b) =>
        this.orden[a.severidad] - this.orden[b.severidad] ||
        new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime(),
    );
  }
}
